import React, { useCallback, useEffect, useState } from 'react'
import { Card, Space, Button, Tag, Form, Input, Modal, Select, InputNumber, DatePicker } from 'antd'
import Table, { type ColumnsType } from 'antd/es/table'
import { type AxiosError } from 'axios'
import { useAppDispatch } from 'Hooks/ReduxHook'
import { PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { setLoader } from 'Store/slices/commonSlice'
import Http, { type ApiErrorData } from 'Util/Http'
import { HTTP_METHOD, type ICoupon } from 'Util/Interface'
import { toast } from 'sonner'

const Coupon: React.FC = () => {
  const [tableData, setTableData] = useState<ICoupon[]>([])
  const [visible, setVisble] = useState(false)
  const [isUpdated, setIsUpdated] = useState<boolean>(false)
  const [updateId, setUpdateId] = useState<string>('')

  const [form] = Form.useForm()

  const dispatch = useAppDispatch()

  const fetch = useCallback(async () => {
    try {
      dispatch(setLoader(true))
      const response = await Http(`/coupon`)
      if (response.data?.status === 'success' && response?.data?.code === 200) {
        dispatch(setLoader(false))
        setTableData(response.data?.data)
      } else {
        setTableData([])
        dispatch(setLoader(false))
      }
    } catch (error: any) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
      setTableData([])
      dispatch(setLoader(false))
    }
  }, [dispatch])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const handleDelete = async (params: ICoupon) => {
    try {
      const response = await Http(`/coupon/${String(params.id)}`, {
        method: 'DELETE',
      })
      if (response.data?.status === 'success' && response?.data?.code === 200) {
        toast.success(response?.data?.message)
        void fetch()
      }
    } catch (error) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
    }
  }

  const handleUpsert = async (param: ICoupon) => {
    try {
      // if (updateId) {
      //   Object.assign(param, { id: updateId })
      // }

      const response = await Http({
        url: !isUpdated ? '/coupon' : `/coupon/${updateId}`,
        method: !isUpdated ? HTTP_METHOD.POST : HTTP_METHOD.PUT,
        data: {
          ...param,
          startDate: dayjs(param.startDate).format('YYYY-MM-DD'),
          endDate: dayjs(param.endDate).format('YYYY-MM-DD'),
        },
      })
      if (response.data?.status === 'success' && response?.data?.code === 200) {
        toast.success(response?.data?.message)
        form.resetFields()
        setVisble(false)
        setIsUpdated(false)
        setUpdateId('')
        void fetch()
      } else {
        toast.error(response?.data?.message ?? 'Something Went Wrong')
      }
    } catch (error) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
    }
  }

  const handleUpdate = (param: ICoupon) => {
    setIsUpdated(true)
    setUpdateId(param.id)
    form.setFieldsValue({
      title: param?.title,
      couponCode: param?.couponCode,
      startDate: dayjs(param?.startDate),
      endDate: dayjs(param?.endDate),
      usageLimit: param?.usageLimit,
      discount: param?.discount,
      status: param?.status,
    })
    setVisble(true)
  }

  const handleCancel = () => {
    setVisble(false)
    setIsUpdated(false)
    setUpdateId('')
    form.resetFields()
  }

  const columns: ColumnsType<ICoupon> = [
    {
      title: 'Sr. No.',
      key: 'serial',
      dataIndex: 'serial',
      render: (_value, _record, index: number) => index + 1,
    },
    {
      title: 'Title',
      key: 'title',
      dataIndex: 'title',
      render: (text: string) => {
        return <span>{text}</span>
      },
    },
    {
      title: 'Coupon Code',
      key: 'couponCode',
      dataIndex: 'couponCode',
    },
    {
      title: 'Start Date',
      key: 'startDate',
      dataIndex: 'startDate',
    },
    {
      title: 'End Date',
      key: 'endDate',
      dataIndex: 'endDate',
    },
    {
      title: 'Usage Limit ',
      key: 'usageLimit',
      dataIndex: 'usageLimit',
    },
    {
      title: 'Discount Amount',
      key: 'discount',
      dataIndex: 'discount',
    },
    {
      title: 'Active',
      key: 'status',
      dataIndex: 'status',
      render: (value) => <Tag color={value === 'Active' ? 'green' : 'red'}>{value}</Tag>,
    },
    {
      title: 'Action',
      dataIndex: 'actions',
      render: (_text, record: ICoupon) => {
        return (
          <Space>
            <Button
              onClick={() => {
                handleUpdate(record)
              }}
            >
              Update
            </Button>
            <Button
              danger
              onClick={() => {
                void handleDelete(record)
              }}
            >
              Delete
            </Button>
          </Space>
        )
      },
    },
  ]

  return (
    <>
      <div className="content-wrapper">
        <Card
          className="card-wrapper"
          title={
            <div className="row justify-between">
              <div>
                <h3>Coupon List</h3>
              </div>
              <div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setVisble(true)
                  }}
                >
                  Add Coupon
                </Button>
              </div>
            </div>
          }
          style={{ padding: '1.25rem 1.25rem 0' }}
        >
          <Table columns={columns} dataSource={tableData} rowKey={(dataIndex: ICoupon) => dataIndex?.id} scroll={{ x: 800 }} bordered size="middle" />
        </Card>
      </div>
      <Modal
        title={!isUpdated ? 'Add Coupon' : 'Update Coupon'}
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Space key={'action'}>
            <Button type="primary" onClick={form.submit} className="btn-padding">
              Save
            </Button>
            <Button danger onClick={handleCancel}>
              Cancel
            </Button>
          </Space>,
        ]}
        width={600}
      >
        <Form
          form={form}
          labelCol={{ span: 7 }}
          wrapperCol={{ span: 14 }}
          autoComplete="off"
          onFinish={(value: ICoupon) => {
            void handleUpsert(value)
          }}
          requiredMark="optional"
        >
          <Form.Item
            label="Package Title"
            name="title"
            rules={[
              {
                required: true,
                message: 'Please Enter Package Title!',
                type: 'string',
              },
            ]}
          >
            <Input className="custom-input" placeholder="Enter Package Title" />
          </Form.Item>

          <Form.Item
            label="Coupon Code"
            name="couponCode"
            rules={[
              {
                required: true,
                message: 'Please Enter Coupon Code!',
                type: 'string',
              },
            ]}
          >
            <Input className="custom-input" placeholder="Enter Coupon Code" />
          </Form.Item>

          <Form.Item
            label="Usage Limit"
            name="usageLimit"
            rules={[
              {
                required: true,
                message: 'Please Enter Usage Limit!',
                type: 'number',
              },
            ]}
          >
            <InputNumber className="custom-input-number" min={0} placeholder="Enter Usage Limit" />
          </Form.Item>

          <Form.Item
            label="Discount Amount"
            name="discount"
            rules={[
              {
                required: true,
                message: 'Please Enter Discount Amount!',
                type: 'number',
              },
            ]}
          >
            <InputNumber className="custom-input-number" min={0} placeholder="Enter Discount Amount" />
          </Form.Item>

          <Form.Item
            label="Start Date"
            name="startDate"
            rules={[
              {
                required: true,
                message: 'Please Select Date!',
              },
            ]}
          >
            <DatePicker
              className="custom-input"
              disabledDate={(current) => {
                return current && current < dayjs().endOf('day')
              }}
              value={dayjs(dayjs(), 'YYYY-MM-DD')}
              format={'YYYY-MM-DD'}
            />
          </Form.Item>

          <Form.Item
            label="End Date"
            name="endDate"
            rules={[
              {
                required: true,
                message: 'Please Select Date!',
              },
            ]}
          >
            <DatePicker
              className="custom-input"
              disabledDate={(current) => {
                return current && current < dayjs().endOf('day')
              }}
              value={dayjs(dayjs(), 'YYYY-MM-DD')}
              format={'YYYY-MM-DD'}
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[
              {
                required: true,
                message: 'Select Status!',
              },
            ]}
          >
            <Select
              placeholder="Select Status"
              className="custom-select"
              style={{ width: '100%' }}
              options={[
                { value: 'Active', label: 'Active' },
                { value: 'InActive', label: 'InActive' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default Coupon
